import {
	BusyIndicator,
	ContextActionBar,
	getPropertySortText,
	OptionsMenu,
	OptionsMenuItem,
	PageContentTitle
} from "client/Components";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState,
} from "react";
import { SharePointBreadCrumb } from "../../SharePoint/SharePointBreadCrumb";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	DocumentsViewModel,
	DocumentsViewType,
	Permissions,
	SearchScope
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import { SharePointFilter, SharePointSearchForm } from "../../SharePoint/SharePointSearchForm";
import { FolderNavigationStackItem } from "../../SharePoint/useFolderNavigationStack";
import { ensureNotNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { GridItem } from "../../SharePoint/SharePointOverView";
import { DocumentPageLayout } from "../../DocumentPageLayout";
import { SharePointPickerOverview } from "./SharePointPickerOverview";
import { CommonItemPickerOptions, InlineCreationOptions } from "../ItemPicker";
import { localize } from "client_legacy/ndaw/culture";
import { NewDocumentDialogInitialData, useNewDocumentDialog } from "../../SharePoint/Dialogs/NewDocumentDialog";
import {
	ActionAndOthers,
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { openNewFolderDialog } from "../../SharePoint/Dialogs/NewFolderDialog";
import { NewDocumentType } from "client/Api/Documents/common";

export type ItemSelectorProps = {
	view: DocumentsViewModel | undefined;
	folderPath: readonly FolderNavigationStackItem[];
	commonOptions: CommonItemPickerOptions;
	onOpenItem: (folder: Folderish) => void;
	onFilesSelected: (files: readonly GridItem[]) => void;
	selectedItems: readonly GridItem[];
	onFolderPermissionsLoaded: (
		viewId: string,
		folderId: number | null,
		permissions: Permissions
	) => void;
	isMobile: boolean;
	currentFolder: SelectedFolder | undefined;
	shouldOpenFileOnClick: boolean;
};

export const ItemSelector = (
	props: ItemSelectorProps
): ReactElement => {
	const {
		view,
		folderPath,
		commonOptions,
		onFolderPermissionsLoaded,
		onOpenItem,
		onFilesSelected,
		selectedItems,
		isMobile,
		currentFolder,
		shouldOpenFileOnClick
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<GridItem>>({
		propertyName: "name",
		direction: DataSourceSortDirection.Ascending
	});

	const [filter, setFilter] = useState<SharePointFilter>({
		scope: SearchScope.Subfolders,
		text: {
			value: "",
			version: newGuid()
		}
	});

	const [newDocumentDialog, openNewDocumentDialog] = useNewDocumentDialog();

	useEffect(
		() => {
			hasImplicitDependencyOn(
				view?.id,
				currentFolder?.id
			);

			setFilter(x => ({
				...x,
				text: {
					value: "",
					version: newGuid()
				}
			}));
		},
		[
			view?.id,
			currentFolder?.id
		]
	);

	const viewKind: DocumentsViewType.EditableView | DocumentsViewType.SharedWithMeView | undefined
		= view?.type === DocumentsViewType.EditableView
			|| view?.type === DocumentsViewType.SharedWithMeView
				? view.type
				: undefined;

	const actions = useMemo(
		(): readonly SharePointPickerAction[] => {
			return getActions(
				onFilesSelected,
				openNewDocumentDialog,
			);
		},
		[onFilesSelected, openNewDocumentDialog]
	);

	const actionContext = useMemo(
		(): SharePointPickerActionContext => {
			return {
				currentFolder,
				selectedItems,
				inlineCreationOptions: commonOptions.creationOptions
					?? { folder: true },
				isMobile
			};
		},
		[
			commonOptions.creationOptions,
			currentFolder,
			isMobile,
			selectedItems
		]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			return selectedItems.length > 0;
		},
		[
			selectedItems.length,
			isMobile
		]
	);

	const renderContent = (): ReactElement | null => {
		if (!view) {
			return (
				<BusyIndicator />
			);
		}

		if (!currentFolder) {
			return null;
		}

		return (
			<SharePointPickerOverview
				folder={currentFolder}
				view={view}
				filter={filter}
				commonOptions={commonOptions}
				onFolderPermissionsLoaded={onFolderPermissionsLoaded}
				onOpenItem={onOpenItem}
				onFilesSelected={onFilesSelected}
				selectedItems={selectedItems}
				isMobile={isMobile}
				sortingState={sortingState}
				setSortingState={setSortingState}
				shouldOpenFileOnClick={shouldOpenFileOnClick}
			/>
		);
	};

	return (
		<DocumentPageLayout trackingId="sharepoint-picker-content">
			<DocumentPageLayout.Header>
				<DocumentPageLayout.BreadCrumb>
					<SharePointBreadCrumb
						items={folderPath}
						onOpenFolder={onOpenItem}
					/>
				</DocumentPageLayout.BreadCrumb>
				{shouldShowActionBar
					? (
						<DocumentPageLayout.ActionBarContainer>
							<ContextActionBar
								actions={actions}
								context={actionContext}
								trackingId="global-action-bar"
								horizontalAlignment={isMobile ? "default" : "page-content"}
								hasBorderBottom={isMobile}
							/>
						</DocumentPageLayout.ActionBarContainer>
					)
					: (
						<PageContentTitle
							actions={actions}
							actionContext={actionContext}
							title={folderPath.length > 0
								? folderPath[folderPath.length - 1].name
								: ""
							}
						/>
					)
				}
				<DocumentPageLayout.SortAndFilter>
					{viewKind !== undefined && (
						<SharePointSearchForm
							viewKind={viewKind}
							isInRootFolder={currentFolder?.isRoot ?? true}
							value={filter}
							onChange={setFilter}
							isMobile={isMobile}
						/>
					)}
					{isMobile && (
						<OptionsMenu
							value={sortingState}
							onChange={setSortingState}
							options={sharePointPickerSortMenuOptions}
							trackingId="sort-menu"
							title={localize("_SortBy")}
							buttonSize="large"
						/>
					)}
				</DocumentPageLayout.SortAndFilter>
			</DocumentPageLayout.Header>
			<DocumentPageLayout.Content>
				{renderContent()}
			</DocumentPageLayout.Content>
			{newDocumentDialog}
		</DocumentPageLayout>
	);
};
ItemSelector.displayName = "ItemSelector";

export type SelectedFolder = Folderish & {
	view: DocumentsViewModel;
};

const sharePointPickerSortMenuOptions: readonly OptionsMenuItem<DataSourceSortableState<GridItem>>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	},
	{
		text: getPropertySortText("_Extension", "_AToZ"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "extension-a-z"
	},
	{
		text: getPropertySortText("_Extension", "_ZToA"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "extension-a-z"
	}
];

type SharePointPickerActionContext = {
	selectedItems: readonly GridItem[];
	currentFolder: SelectedFolder | undefined;
	inlineCreationOptions: InlineCreationOptions;
	isMobile: boolean;
};

type SharePointPickerAction = ActionAndOthers<SharePointPickerActionContext>;

function getCanCreatePermission(currentFolder: SelectedFolder | undefined): boolean {
	return Boolean(
		currentFolder?.permissions
			?.canCreate
	);
};

function getActions(
	onFilesSelected: (files: readonly GridItem[]) => void,
	openNewDocumentDialog: (props: NewDocumentDialogInitialData) => Promise<void>
): readonly SharePointPickerAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => deselectAllTitle(c.selectedItems.length),
			canExecute: (c) => c.selectedItems.length > 0,
			execute: () => onFilesSelected([]),
			icon: "circle-xmark"
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		},
		{
			kind: ActionKind.Regular,
			name: "new-folder",
			title: localize("_NewFolder"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.folder)
				&& getCanCreatePermission(c.currentFolder),
			execute: (c) => openNewFolderDialog({ parentFolder: ensureNotNil(c.currentFolder) }),
			icon: { kind: "svg", name: "folder" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-word-document",
			title: localize("_NewWordDocument"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.word)
				&& getCanCreatePermission(c.currentFolder),
			execute: (c) => openNewDocumentDialog(
				{
					parentFolder: ensureNotNil(c.currentFolder),
					documentType: NewDocumentType.Word
				}
			),
			icon: { kind: "svg", name: "docx" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-excel-document",
			title: localize("_NewExcelWorksheet"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.excel)
				&& getCanCreatePermission(c.currentFolder),
			execute: (c) => openNewDocumentDialog(
				{
					parentFolder: ensureNotNil(c.currentFolder),
					documentType: NewDocumentType.Excel
				}
			),
			icon: { kind: "svg", name: "xlsx" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-power-point-document",
			title: localize("_NewPowerPointPresentation"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.powerpoint)
				&& getCanCreatePermission(c.currentFolder),
			execute: (c) => openNewDocumentDialog(
				{
					parentFolder: ensureNotNil(c.currentFolder),
					documentType: NewDocumentType.PowerPoint
				}
			),
			icon: { kind: "svg", name: "powerpoint" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-one-note-document",
			title: localize("_NewOneNoteNotebook"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.onenote)
				&& getCanCreatePermission(c.currentFolder),
			execute: (c) => openNewDocumentDialog(
				{
					parentFolder: ensureNotNil(c.currentFolder),
					documentType: NewDocumentType.OneNote
				}
			),
			icon: { kind: "svg", name: "onenote" }
		}
	];
}
